import { createStore } from 'vuex';
import Axios from 'axios';
import {
  getIssuer,
  getIssuerTemplates,
  getIssuerTemplate,
  getIssuerTypeTemplates,
} from '../plugins/api';
import { hidesVerticalBars } from '../utils/marketingPages';
// Client léger pour /auth/me (sans intercepteurs qui pourraient créer des boucles)
// En dev avec proxy : toujours même origine ('' = page actuelle) pour que les cookies HttpOnly soient envoyés
const _backendBase = (process.env.VUE_APP_BACKEND_URL || '').replace(/\/+$/, '');
let _authBase = _backendBase.endsWith('/api/v1') ? _backendBase.slice(0, -7) : _backendBase;
const isLocalhost = typeof window !== 'undefined' && /^localhost$|^127\.0\.0\.1$/.test(window.location.hostname);
if (isLocalhost && _authBase.startsWith('http') && !_authBase.startsWith(window.location.origin)) {
  _authBase = ''; // dev : requêtes relatives → proxy → cookies OK
}
const authClient = Axios.create({
  baseURL: _authBase,
  withCredentials: true,
  timeout: (() => {
    const n = Number(process.env.VUE_APP_AUTH_ME_TIMEOUT_MS || 10000);
    if (!Number.isFinite(n) || n <= 0) return 10000;
    return Math.max(3000, Math.min(Math.round(n), 30000));
  })(),
});

function isBootDebugEnabled() {
  try {
    if (typeof window === 'undefined') return false;
    const qp = new URLSearchParams(window.location.search).get('debugBoot');
    if (qp === '1' || qp === 'true') return true;
    return localStorage.getItem('debug_boot') === '1';
  } catch (_err) {
    return false;
  }
}

// ============================================
// LocalStorage Cache Helpers
// ============================================
const CACHE_VERSION = '1.0';
const CACHE_MAX_AGE = 60 * 60 * 1000; // 1 heure

function getCacheKey(type) {
  return `template_list_${type.toLowerCase()}_v${CACHE_VERSION}`;
}

function getTemplateListFromCache(type) {
  try {
    const key = getCacheKey(type);
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);

    // Vérifier si le cache est encore valide
    if (Date.now() - timestamp > CACHE_MAX_AGE) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch (e) {
    console.warn('Error reading from localStorage cache:', e);
    return null;
  }
}

function saveTemplateListToCache(type, data) {
  try {
    const key = getCacheKey(type);
    const cached = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cached));
  } catch (e) {
    // localStorage peut être plein ou indisponible
    console.warn('Could not save to localStorage:', e);
  }
}

function clearTemplateCache(type = null) {
  try {
    if (type) {
      // Clear specific type
      const key = getCacheKey(type);
      localStorage.removeItem(key);
    } else {
      // Clear all template caches
      const types = ['Basic', 'Community', 'Participation', 'Ticket', 'Membership', 'Role'];
      types.forEach(t => {
        const key = getCacheKey(t);
        localStorage.removeItem(key);
      });
    }
  } catch (e) {
    console.warn('Error clearing cache:', e);
  }
}
// ============================================

// ============================================
// Cache Helpers Génériques pour Pages Communauté
// ============================================
function getGenericCacheKey(pageName) {
  return `community_${pageName}_v${CACHE_VERSION}`;
}

function getDataFromCache(pageName) {
  try {
    const key = getGenericCacheKey(pageName);
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);

    // Vérifier si le cache est encore valide
    if (Date.now() - timestamp > CACHE_MAX_AGE) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch (e) {
    console.warn(`Error reading ${pageName} from localStorage cache:`, e);
    return null;
  }
}

function saveDataToCache(pageName, data) {
  try {
    const key = getGenericCacheKey(pageName);
    const cached = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cached));
  } catch (e) {
    console.warn(`Could not save ${pageName} to localStorage:`, e);
  }
}

function clearDataCache(pageName) {
  try {
    const key = getGenericCacheKey(pageName);
    localStorage.removeItem(key);
  } catch (e) {
    console.warn(`Error clearing ${pageName} cache:`, e);
  }
}
// ============================================

// ============================================
// Cache Helpers pour Brand Analyzer (avec DID)
// ============================================
function getBrandAnalyzerCacheKey(did, platform, username) {
  // Format: brand_analyzer_{DID}_{platform}_{username}_v1.0
  return `brand_analyzer_${did}_${platform}_${username}_v${CACHE_VERSION}`;
}

function getBrandAnalyzerFromCache(did, platform, username) {
  try {
    const key = getBrandAnalyzerCacheKey(did, platform, username);
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);

    // Vérifier si le cache est encore valide
    if (Date.now() - timestamp > CACHE_MAX_AGE) {
      localStorage.removeItem(key);
      return null;
    }

    return data;
  } catch (e) {
    console.warn(`Error reading brand analyzer cache for ${did}/${platform}/${username}:`, e);
    return null;
  }
}

function saveBrandAnalyzerToCache(did, platform, username, data) {
  try {
    const key = getBrandAnalyzerCacheKey(did, platform, username);
    const cached = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(key, JSON.stringify(cached));
  } catch (e) {
    console.warn(`Could not save brand analyzer cache for ${did}/${platform}/${username}:`, e);
  }
}

function clearBrandAnalyzerCache(did, platform, username) {
  try {
    const key = getBrandAnalyzerCacheKey(did, platform, username);
    localStorage.removeItem(key);
  } catch (e) {
    console.warn(`Error clearing brand analyzer cache for ${did}/${platform}/${username}:`, e);
  }
}
// ============================================

const store = createStore({
  state: {
    // Session courante (remplie par /auth/me ou par les métadonnées de login)
    // { did, connexionEmail, web2, isContributor, isAdmin, isTmp, isSubCommunity, exp, state }
    sessionInfo: null,
    // Timestamp du dernier appel /auth/me (cache négatif : évite les appels répétés quand null)
    sessionLastCheck: 0,

    currentIssuerDid: null,
    currentIssuerInfo: null,
    currentAddress: null,
    currentPublicKeyBase58: null,
    currentChainId: null,
    isCurrentChainIdValid: false,
    isWeb2Connexion: false,
    isAdmin: false,
    connexionEmail: null,

    displayIssuerConnection: false,
    displayVerticalBars:
      typeof window !== 'undefined'
        ? !hidesVerticalBars(window.location.pathname)
        : true,
    tourOngoing: false,
    navbarCollapsed: false,

    templateListLoading: null,
    basicTemplateList: null,
    communityTemplateList: null,
    participationTemplateList: null,
    ticketTemplateList: null,
    membershipTemplateList: null,
    roleTemplateList: null,

    // Cache pour les pages communauté
    cachedMembers: null,
    cachedQuests: null,
    cachedSubCommunities: null,
    cachedBadgeDetails: {}, // Stockage par templateId
    cachedChannelMessages: null,

    // Cache pour Brand Analyzer (par DID + platform + username)
    cachedBrandAnalyzer: {}, // Structure: { 'DID_platform_username': { data, timestamp }, ... }

    // Modal envoi badge (affiché au niveau App pour éviter les problèmes de rendu)
    sendModalTemplate: null,
  },
  mutations: {
    setSessionInfo(state, payload) {
      state.sessionInfo = payload;
      // Synchroniser les champs Vuex existants depuis sessionInfo
      if (payload) {
        state.isWeb2Connexion = !!payload.web2;
        state.isAdmin = !!payload.isAdmin;
        state.connexionEmail = payload.connexionEmail || null;
      }
    },
    clearSessionInfo(state) {
      state.sessionInfo = null;
      state.sessionLastCheck = 0; // reset du cache négatif
    },
    setCurrentIssuerDid(state, payload) {
      const didChanged = payload && payload !== state.currentIssuerDid;
      state.currentIssuerDid = payload;
      state.displayIssuerConnection = false;
      if (didChanged) {
        state.cachedMembers = null;
        state.cachedQuests = null;
        state.cachedSubCommunities = null;
        state.cachedChannelMessages = null;
        state.cachedBadgeDetails = {};
        state.basicTemplateList = null;
        state.communityTemplateList = null;
        state.participationTemplateList = null;
        state.ticketTemplateList = null;
        state.membershipTemplateList = null;
        state.roleTemplateList = null;
        clearTemplateCache();
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('community_members_v1.0');
            localStorage.removeItem('community_quests_v1.0');
            localStorage.removeItem('community_subcommunities_v1.0');
            localStorage.removeItem('community_subcommunities_v1.1');
            localStorage.removeItem('community_channel_messages_v1.0');
            Object.keys(localStorage).forEach((key) => {
              if (key.startsWith('community_subcommunities_')) {
                localStorage.removeItem(key);
              }
              if (key.startsWith('community_channel_messages_')) {
                localStorage.removeItem(key);
              }
            });
          }
        } catch (_) { }
      }
    },
    setCurrentIssuerInfo(state, payload) {
      state.currentIssuerInfo = payload;
    },
    setCurrentAddress(state, payload) {
      state.currentAddress = payload;
    },
    setCurrentPublicKeyBase58(state, payload) {
      state.currentPublicKeyBase58 = payload;
    },
    setCurrentChainId(state, payload) {
      state.currentChainId = payload;
      state.isCurrentChainIdValid =
        payload == Number(process.env.VUE_APP_CHAIN_ID);
    },
    setIsWeb2Connexion(state, payload) {
      state.isWeb2Connexion = payload;
    },
    setIsAdmin(state, payload) {
      state.isAdmin = payload;
    },
    setConnexionEmail(state, payload) {
      state.connexionEmail = payload;
    },
    resetIssuerState(state) {
      // Ne pas toucher à sessionInfo ici — il est géré indépendamment
      state.currentIssuerDid = null;
      state.currentAddress = null;
      state.currentPublicKeyBase58 = null;
      state.currentIssuerInfo = null;
      state.currentChainId = null;
      state.isCurrentChainIdValid = false;
      state.connexionEmail = null;
    },
    setIssuerConnectionDisplay(state, payload) {
      state.displayIssuerConnection = payload;
    },
    setVerticalBarsDisplay(state, payload) {
      state.displayVerticalBars = payload;
    },
    setTourOngoing(state, payload) {
      state.tourOngoing = payload;
    },
    setNavbarCollapsed(state, payload) {
      state.navbarCollapsed = payload;
    },
    setTemplateListLoading(state, payload) {
      state.templateListLoading = payload;
    },
    setBasicTemplateList(state, payload) {
      state.basicTemplateList = payload;
    },
    setCommunityTemplateList(state, payload) {
      state.communityTemplateList = payload;
    },
    setParticipationTemplateList(state, payload) {
      state.participationTemplateList = payload;
    },
    setTicketTemplateList(state, payload) {
      state.ticketTemplateList = payload;
    },
    setMembershipTemplateList(state, payload) {
      state.membershipTemplateList = payload;
    },
    setRoleTemplateList(state, payload) {
      state.roleTemplateList = payload;
    },
    // Mutations pour cache communauté
    setCachedMembers(state, payload) {
      state.cachedMembers = payload;
    },
    setCachedQuests(state, payload) {
      state.cachedQuests = payload;
    },
    setCachedSubCommunities(state, payload) {
      state.cachedSubCommunities = payload;
    },
    setCachedBadgeDetail(state, { templateId, data }) {
      state.cachedBadgeDetails[templateId] = data;
    },
    openSendModal(state, template) {
      state.sendModalTemplate = template;
    },
    closeSendModal(state) {
      state.sendModalTemplate = null;
    },
    clearCachedBadgeDetail(state, templateId) {
      if (state.cachedBadgeDetails[templateId]) {
        delete state.cachedBadgeDetails[templateId];
      }
    },
    setCachedChannelMessages(state, payload) {
      state.cachedChannelMessages = payload;
    },

    // Mutations pour Brand Analyzer cache (avec DID)
    setCachedBrandAnalyzer(state, { did, platform, username, data }) {
      const key = `${did}_${platform}_${username}`;
      state.cachedBrandAnalyzer[key] = {
        data,
        timestamp: Date.now()
      };
    },
    clearCachedBrandAnalyzer(state, { did, platform, username }) {
      const key = `${did}_${platform}_${username}`;
      if (state.cachedBrandAnalyzer[key]) {
        delete state.cachedBrandAnalyzer[key];
      }
    },
    clearAllBrandAnalyzerCache(state) {
      state.cachedBrandAnalyzer = {};
    },
    // Nettoyer le cache d'un DID spécifique (quand on change de communauté)
    clearBrandAnalyzerCacheByDid(state, did) {
      Object.keys(state.cachedBrandAnalyzer).forEach(key => {
        if (key.startsWith(`${did}_`)) {
          delete state.cachedBrandAnalyzer[key];
        }
      });
    },
  },
  actions: {
    // Charge la session depuis /auth/me (appelé par le router guard et App.vue)
    // Retourne sessionInfo ou null
    async initSession({ commit, state }, options = {}) {
      const forceNetwork = options?.forceNetwork === true;
      const clearOnUnauthenticated = options?.clearOnUnauthenticated === true;
      const debugBoot = isBootDebugEnabled();
      const startTs = Date.now();
      if (debugBoot) {
        console.info('[BOOT][store] initSession:start');
      }
      // Cache positif : session encore valide (et pas expirée)
      const nowSec = Date.now() / 1000;
      if (!forceNetwork && state.sessionInfo) {
        // Si exp est défini et valide (> maintenant), retourner le cache
        if (!state.sessionInfo.exp || state.sessionInfo.exp > nowSec) {
          if (debugBoot) {
            console.info('[BOOT][store] initSession:cached_valid');
          }
          return state.sessionInfo;
        }
        // Session expirée → continuer vers /auth/me
        commit('clearSessionInfo');
      }
      // Cache négatif : on a déjà vérifié il y a moins de 10 s et il n'y avait rien
      const NEGATIVE_CACHE_MS = 10_000;
      if (!forceNetwork && !state.sessionInfo && state.sessionLastCheck && Date.now() - state.sessionLastCheck < NEGATIVE_CACHE_MS) {
        if (debugBoot) {
          console.info('[BOOT][store] initSession:negative_cache');
        }
        return null;
      }
      state.sessionLastCheck = Date.now();
      try {
        const res = await authClient.get('/api/v1/auth/me');
        const payload = res?.data || {};
        const hasSession =
          (payload.authenticated === true && !!payload.session) ||
          (payload.status === 'success' && !!payload.session);

        if (hasSession) {
          const session = payload.session;
          commit('setSessionInfo', session);
          if (debugBoot) {
            console.info('[BOOT][store] initSession:success', {
              isTmp: !!session?.isTmp,
              did: session?.did || null,
              durationMs: Date.now() - startTs,
            });
          }
          return session;
        }

        // Nouveau format silencieux: authenticated=false (HTTP 200)
        // Ancien format de compat: status=unauthenticated
        if (
          payload.authenticated === false ||
          payload.status === 'unauthenticated'
        ) {
          if (clearOnUnauthenticated) {
            commit('clearSessionInfo');
          }
          if (debugBoot) {
            console.info('[BOOT][store] initSession:unauthenticated', {
              durationMs: Date.now() - startTs,
            });
          }
          return null;
        }
      } catch (error) {
        // 401 ou erreur réseau → pas de session (silence intentionnel)
        if (
          clearOnUnauthenticated &&
          [401, 403].includes(error?.response?.status)
        ) {
          commit('clearSessionInfo');
        }
        if (debugBoot) {
          console.warn('[BOOT][store] initSession:error', {
            message: error?.message || 'unknown_error',
            code: error?.code || null,
            status: error?.response?.status || null,
            durationMs: Date.now() - startTs,
          });
        }
      }
      // Ne pas écraser une session existante si elle est encore présente
      // (peut être setée par finalizeMagicAuth pendant l'appel /auth/me)
      if (state.sessionInfo) {
        if (debugBoot) {
          console.info('[BOOT][store] initSession:kept_existing_session');
        }
        return state.sessionInfo;
      }
      if (debugBoot) {
        console.info('[BOOT][store] initSession:null_session', {
          durationMs: Date.now() - startTs,
        });
      }
      return null;
    },
    async getIssuerInfo({ commit, state }) {
      try {
        const issuerInfo = (await getIssuer()).data;
        commit('setCurrentIssuerInfo', issuerInfo);
        return { success: true };
      } catch (error) {
        console.error('Error fetching issuer info:', error);
        // Ne pas planter l'application si l'API n'est pas disponible
        // L'erreur est déjà gérée par l'intercepteur axios qui affiche un toast
        return { success: false, error };
      }

      // Données fictives pour le développement front-end (désactivées)
      // const mockIssuerInfo = {
      //   did: 'did:mydid:mock123',
      //   name: 'Ma Communauté',
      //   description: 'Description de ma communauté fictive',
      //   website: 'https://example.com',
      //   sector: 'Technology',
      //   bannerColor: '#4F46E5',
      //   twitter: 'https://twitter.com/example',
      //   discord: 'https://discord.gg/example',
      //   telegram: 'https://t.me/example',
      //   instagram: 'https://instagram.com/example',
      //   linkedin: 'https://linkedin.com/company/example',
      //   active: true,
      // };
      // commit('setCurrentIssuerInfo', mockIssuerInfo);
    },
    async getTemplateLists({ commit, state }) {
      commit('setTemplateListLoading', true);

      try {
        const list = (await getIssuerTemplates(state.currentAddress)).data;

        commit(
          'setBasicTemplateList',
          list.filter((template) => template.data.badgeCategory == 'Basic')
        );
        commit(
          'setCommunityTemplateList',
          list.filter((template) => template.data.badgeCategory == 'Community')
        );
        commit(
          'setParticipationTemplateList',
          list.filter(
            (template) => template.data.badgeCategory == 'Participation'
          )
        );
        commit(
          'setTicketTemplateList',
          list.filter((template) => template.data.badgeCategory == 'Ticket')
        );
        commit(
          'setMembershipTemplateList',
          list.filter((template) => template.data.badgeCategory == 'Membership')
        );
        commit(
          'setRoleTemplateList',
          list.filter((template) => template.data.badgeCategory == 'Role')
        );
      } catch (error) {
        console.error('Error fetching template lists:', error);
        // Initialiser les listes vides en cas d'erreur
        commit('setBasicTemplateList', []);
        commit('setCommunityTemplateList', []);
        commit('setParticipationTemplateList', []);
        commit('setTicketTemplateList', []);
        commit('setMembershipTemplateList', []);
        commit('setRoleTemplateList', []);
        // L'erreur est déjà gérée par l'intercepteur axios qui affiche un toast
        return { success: false, error };
      } finally {
        commit('setTemplateListLoading', false);
      }
    },
    async refreshTemplateList({ commit, state }, { type }) {
      try {
        // 🚀 Essayer de charger depuis localStorage d'abord
        const cachedData = getTemplateListFromCache(type);
        if (cachedData) {
          commit(`set${type}TemplateList`, cachedData);
        }

        // Charger depuis l'API (toujours pour avoir les données fraîches)
        const list = (await getIssuerTypeTemplates(type)).data;
        commit(`set${type}TemplateList`, list);

        // Sauvegarder dans localStorage
        saveTemplateListToCache(type, list);

      } catch (error) {
        // Si on a un cache et que l'API échoue, on garde le cache
        // L'erreur est déjà gérée par l'intercepteur axios
      }
    },
    async refreshTemplate({ commit, state }, { id }) {
      try {
        const template = (await getIssuerTemplate(id)).data;
        const list =
          state[`${template.data.badgeCategory.toLowerCase()}TemplateList`];
        const index = list.findIndex((t) => t.id == id);

        commit(`set${template.data.badgeCategory}TemplateList`, [
          ...list.slice(0, index),
          template,
          ...list.slice(index + 1),
        ]);

        // Mettre à jour le cache localStorage
        const updatedList = [
          ...list.slice(0, index),
          template,
          ...list.slice(index + 1),
        ];
        saveTemplateListToCache(template.data.badgeCategory, updatedList);

      } catch (error) {
        console.error('Error refreshing template:', error);
        // L'erreur est déjà gérée par l'intercepteur axios
      }
    },
    clearTemplateCache({ commit }, { type }) {
      clearTemplateCache(type);
    },
  },
  modules: {},
  getters: {
    getSessionInfo(state) {
      return state.sessionInfo;
    },
    isAuthenticated(state) {
      return !!state.sessionInfo && !state.sessionInfo.isTmp;
    },
    isAuthenticatedOrTmp(state) {
      return !!state.sessionInfo;
    },
    getCurrentIssuerDid(state) {
      return state.currentIssuerDid;
    },
    getCurrentIssuerInfo(state) {
      return state.currentIssuerInfo;
    },
    getCurrentAddress(state) {
      return state.currentAddress;
    },
    getCurrentPublicKeyBase58(state) {
      return state.currentPublicKeyBase58;
    },
    getCurrentChainId(state) {
      return state.currentChainId;
    },
    isCurrentChaindIdValid(state) {
      return state.isCurrentChainIdValid;
    },
    isWeb2Connexion(state) {
      return state.isWeb2Connexion;
    },
    /** true si connexion via QR / app mobile (Nostr), false si email/KMS */
    isWeb3Connexion(state) {
      return !state.isWeb2Connexion;
    },
    /** 'web2' = email/KMS (clés serveur), 'web3' = QR/app mobile (Nostr, clés app) */
    connectionMode(state) {
      return state.isWeb2Connexion ? 'web2' : 'web3';
    },
    isAdmin(state) {
      return state.isAdmin;
    },
    getConnexionEmail(state) {
      return state.connexionEmail;
    },
    isIssuerConnectionDisplayed(state) {
      return state.displayIssuerConnection;
    },
    areVerticalBarsDisplayed(state) {
      return state.displayVerticalBars;
    },
    isTourOngoing(state) {
      return state.tourOngoing;
    },
    isNavbarCollapsed(state) {
      return state.navbarCollapsed;
    },
    getTemplateListLoading(state) {
      return state.templateListLoading;
    },
    getBasicTemplateList(state) {
      return state.basicTemplateList;
    },
    getCommunityTemplateList(state) {
      return state.communityTemplateList;
    },
    getParticipationTemplateList(state) {
      return state.participationTemplateList;
    },
    getTicketTemplateList(state) {
      return state.ticketTemplateList;
    },
    getMembershipTemplateList(state) {
      return state.membershipTemplateList;
    },
    getRoleTemplateList(state) {
      return state.roleTemplateList;
    },
    getSendModalTemplate(state) {
      return state.sendModalTemplate;
    },

    // ─────────────────────────────────────────────
    // OFFRE – source de vérité : backend via sessionInfo.offer
    // Lecture seule, jamais modifiable via le store
    // ─────────────────────────────────────────────

    /** Retourne l'objet complet de l'offre sans figer l'état réactif Vuex */
    getDefaultOffer: (state) => {
      const offer = state.sessionInfo?.offer;
      if (!offer) return null;
      return {
        ...offer,
        limits: { ...(offer.limits || {}) },
        features: { ...(offer.features || {}) },
        grandfathering: { ...(offer.grandfathering || {}) },
        cronHours: { ...(offer.cronHours || {}) },
      };
    },

    /** Retourne toutes les limites de l'offre */
    getOfferLimits: (state) => ({ ...(state.sessionInfo?.offer?.limits || {}) }),

    /** Retourne tous les feature flags de l'offre */
    getOfferFeatures: (state) => ({ ...(state.sessionInfo?.offer?.features || {}) }),

    /** Retourne la politique de grandfathering */
    getOfferGrandfathering: (state) => ({ ...(state.sessionInfo?.offer?.grandfathering || {}) }),

    /**
     * Retourne la valeur d'une limite spécifique.
     * Usage : store.getters.getOfferLimit('members')
     * → null (non défini / illimité pour l'instant) | -1 (illimité) | N (limite active)
     */
    getOfferLimit: (state) => (key) => {
      const limits = state.sessionInfo?.offer?.limits;
      if (!limits) return null;
      return limits[key] ?? null;
    },

    /**
     * Retourne true si une limite est active (≥ 0) pour la clé donnée.
     * null = non encore défini (pas de restriction), -1 = illimité
     * Usage : store.getters.isOfferLimitActive('members')
     */
    isOfferLimitActive: (state) => (key) => {
      const limits = state.sessionInfo?.offer?.limits;
      if (!limits) return false;
      const val = limits[key] ?? null;
      return val !== null && val >= 0;
    },

    /**
     * Retourne true si un feature est activé.
     * Usage : store.getters.isOfferFeatureEnabled('brandAnalyzer')
     */
    isOfferFeatureEnabled: (state) => (key) => {
      return !!state.sessionInfo?.offer?.features?.[key];
    },
  },
});

// Exporter les fonctions de cache pour utilisation dans les composants
export {
  getDataFromCache,
  saveDataToCache,
  clearDataCache,
  getBrandAnalyzerFromCache,
  saveBrandAnalyzerToCache,
  clearBrandAnalyzerCache
};

export default store;
